import React, { createContext, useContext } from 'react';
import { Modal, Box, IconButton, ButtonBase, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from '../../store/modalSlice';
import { useTheme } from '@mui/material/styles';
import chroma from 'chroma-js';
import GlassButton from '../GlassButton';
import Text from '../Text';
import SegmentedTabs from './SegmentedTabs';
import { Close, Fullscreen, FullscreenExit, RestartAlt } from '@mui/icons-material';
import GroupsSelectorElement from '../GroupsSelectorElement';
import { useTranslation } from 'react-i18next';
import { useDisplayContext } from '../../hooks/useDisplayContext';
import { ModalHeaderContext } from './ModalHeader';
import { usePosKeyboard } from '../../providers/PosKeyboardProvider';
import { glassChromeControl, glassChromeRadii, glassChromeSurface } from '../../theme/glassChrome';

const ModalFullscreenContext = createContext(false);
export const useModalFullscreen = () => useContext(ModalFullscreenContext);

const ModalTemplate = ({
  children,
  title: providedTitle,
  subtitle: providedSubtitle,
  headerActions,
  defaultFullscreen = false,
  topTitle,
  icon,
  primaryAction,
  secondaryAction,
  onPrimaryAction,
  onSecondaryAction,
  width,
  height,
  maxHeight,
  minHeight,
  groups,
  setGroups,
  disabledPrim,
  disabledSec = false,
  onClose,
  onReset,
  showReset = false,
  noScroll,
  noPadding,
  isNested = false,
  disableClose = false,
  tabs,
  activeTab,
  onTabChange,
}) => {
  const [isFullscreen, setIsFullscreen] = React.useState(defaultFullscreen);
  const [headerSlot,setHeaderSlot]=React.useState(null),[heading,setHeading]=React.useState(null);
  const headerContext=React.useMemo(()=>({slot:headerSlot,setHeading}),[headerSlot]);
  const title=heading?.title ?? providedTitle,subtitle=heading?.subtitle ?? providedSubtitle;
  React.useEffect(()=>{setIsFullscreen(defaultFullscreen);},[defaultFullscreen]);
  const headerExtras = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: { xs: 1, sm: 0 }, overflowX: { xs: 'auto', sm: 'visible' } }}>
      {headerActions}
      <Box ref={setHeaderSlot} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }} />
    </Box>
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const displayCtx = useDisplayContext();
  const isPos = displayCtx?.isPos;
  const posKeyboard = usePosKeyboard();
  const isKeyboardOpen = isPos && posKeyboard?.isKeyboardOpen;
  const businessData = useSelector((state) => state.core.businessData);
  const mainColor = businessData?.mainColor || theme.palette.primary.main;
  const secColor = businessData?.secColor || theme.palette.secondary.main;

  const isPrintModal =
    (typeof subtitle === 'string' &&
      subtitle.toLowerCase().includes('print')) ||
    (typeof topTitle === 'string' &&
      topTitle.toLowerCase().includes('print'));

  // Some modals run an irreversible operation and must not be dismissed
  // mid-flight, neither by the close button nor by the backdrop.
  const handleClose = () => {
    if (disableClose) return;
    if (isPos && posKeyboard?.dismiss) posKeyboard.dismiss();
    dispatch(closeModal());
    onClose?.();
  };

  const handleGroupsChange = (value, ids) => {
    setGroups(ids);
  };

  const textMuted = theme.palette.text.secondary;
  const controlStyles = glassChromeControl(theme, { size: isMobile ? 36 : 32 });

  // ── POS: fullscreen modal ──
  if (isPos) {
    const borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';

    return (
      <ModalHeaderContext.Provider value={headerContext}><Modal
        open
        onClose={handleClose}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'transparent',
            },
          },
        }}
      >
        <Box
          sx={{
            ...glassChromeSurface(theme, { radius: 0 }),
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            background: theme.palette.background.paper,
            border: 0,
            boxShadow: 'none',
            opacity: 0,
            animation: 'posModalIn 200ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            '@keyframes posModalIn': {
              from: { opacity: 0, transform: 'translateY(8px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              transition: 'none',
              opacity: 1,
              transform: 'none',
            },
          }}
        >
          {/* ── Top Bar ── */}
          <Box
            sx={{
              ...glassChromeSurface(theme, { radius: 0, blur: true }),
              display: 'flex',
              alignItems: 'center',
              height: 56,
              flexShrink: 0,
              border: 0,
              borderBottom: `1px solid ${borderColor}`,
              boxShadow: 'none',
            }}
          >
            {!disableClose && (
              <ButtonBase
                onClick={handleClose}
                aria-label={t('close', 'Fermer')}
                sx={{
                  ...glassChromeControl(theme, { size: 56, radius: 0 }),
                  flexShrink: 0,
                  border: 0,
                  borderRight: `1px solid ${borderColor}`,
                  boxShadow: 'none',
                }}
              >
                <Close sx={{ fontSize: 22 }} />
              </ButtonBase>
            )}

            {/* Title */}
            <Box sx={{ flex: 1, px: 2, minWidth: 0 }}>
              <Text
                value={topTitle || title || ''}
                fontSize={16}
                fontWeight={700}
                sx={{ fontFamily: 'Montserrat', letterSpacing: '-0.01em', lineHeight: 1.3 }}
              />
              {subtitle && (
                <Text
                  value={subtitle}
                  fontSize={11}
                  fontWeight={400}
                  sx={{ color: textMuted, lineHeight: 1.3, mt: '-1px' }}
                />
              )}
            </Box>

            {headerExtras}
            {/* Groups selector */}
            {groups !== undefined && (
              <Box sx={{ width: 200, px: 1, borderLeft: `1px solid ${borderColor}` }}>
                <GroupsSelectorElement
                  label={t('allowedGroups')}
                  value={groups}
                  onChange={handleGroupsChange}
                />
              </Box>
            )}

            {/* Reset action */}
            {showReset && onReset && (
              <ButtonBase
                onClick={onReset}
                title={t('reset') || 'Reset'}
                aria-label={t('reset') || 'Reset'}
                sx={{
                  ...glassChromeControl(theme, { size: 56, radius: 0 }),
                  flexShrink: 0,
                  border: 0,
                  borderLeft: `1px solid ${borderColor}`,
                  boxShadow: 'none',
                }}
              >
                <RestartAlt sx={{ fontSize: 21 }} />
              </ButtonBase>
            )}

            {/* Secondary action */}
            {secondaryAction && (
              <ButtonBase
                onClick={onSecondaryAction}
                disabled={disabledSec}
                sx={{
                  height: 56,
                  px: 2.5,
                  borderLeft: `1px solid ${borderColor}`,
                  color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                  fontSize: 14,
                  fontWeight: 600,
                  '&:active': { background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
                }}
              >
                {secondaryAction}
              </ButtonBase>
            )}

            {/* Primary action */}
            {primaryAction && (
              <ButtonBase
                onClick={onPrimaryAction}
                disabled={disabledPrim}
                sx={{
                  height: 56,
                  px: 3,
                  borderLeft: `1px solid ${borderColor}`,
                  background: disabledPrim
                    ? (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')
                    : `linear-gradient(135deg, ${mainColor}, ${secColor})`,
                  color: disabledPrim
                    ? (isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)')
                    : '#fff',
                  fontSize: 15,
                  fontWeight: 700,
                  opacity: disabledPrim ? 0.6 : 1,
                  '&:active': { opacity: 0.85 },
                }}
              >
                {primaryAction}
              </ButtonBase>
            )}
          </Box>

          {tabs?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                px: 2,
                py: 1,
                flexShrink: 0,
                borderBottom: `1px solid ${borderColor}`,
              }}
            >
              <SegmentedTabs
                items={tabs}
                value={activeTab}
                onChange={onTabChange}
                isPos={isPos}
              />
            </Box>
          )}

          {/* ── Content ── */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: noScroll ? 'hidden' : 'auto',
              p: noPadding ? 0 : 3,
              pb: isKeyboardOpen ? '50vh' : (noPadding ? 0 : 3),
              transition: 'padding-bottom 200ms ease',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
              },
            }}
          >
            {children}
          </Box>
        </Box>
      </Modal></ModalHeaderContext.Provider>
    );
  }

  // ── Standard (non-POS) modal ──
  const resolvedWidth =
    isMobile && !isPrintModal
      ? '92%'
      : isNested
        ? 1300
        : isPrintModal
          ? 1300
          : 650;

  return (
    <ModalHeaderContext.Provider value={headerContext}><Modal
      open
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: isDarkMode
              ? 'rgba(0,0,0,0.55)'
              : 'rgba(0,0,0,0.2)',
            backdropFilter: isPrintModal ? 'none' : 'blur(6px)',
            WebkitBackdropFilter: isPrintModal ? 'none' : 'blur(6px)',
            '@media (prefers-reduced-transparency: reduce)': {
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
            },
          },
        },
      }}
    >
        <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 300ms cubic-bezier(0.16, 1, 0.3, 1), height 300ms cubic-bezier(0.16, 1, 0.3, 1), max-height 300ms cubic-bezier(0.16, 1, 0.3, 1)',
          transform: 'translate(-50%, -50%)',
          ...(isFullscreen
            ? {
                width: '100vw',
                height: '100dvh',
                maxHeight: '100dvh',
              }
            : {
                width: width || resolvedWidth,
                minHeight: minHeight || height || null,
                maxHeight: maxHeight || (primaryAction ? '78vh' : '82vh'),
              }),
          opacity: 0,
          animation: 'modalTemplateIn 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
          '@keyframes modalTemplateIn': {
            from: {
              opacity: 0,
              transform: 'translate(-50%, -48%) scale(0.97)',
            },
            to: {
              opacity: 1,
              transform: 'translate(-50%, -50%) scale(1)',
            },
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            transition: 'none',
            opacity: 1,
            transform: 'translate(-50%, -50%)',
          },
        }}
      >
        {/* Main surface */}
        <Box
          sx={{
            ...glassChromeSurface(theme, {
              radius: isFullscreen ? 0 : glassChromeRadii.modal,
              blur: !isPrintModal,
              elevated: !isPrintModal,
            }),
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(isFullscreen && { border: 0 }),
            ...(isPrintModal && {
              background: theme.palette.background.paper,
              boxShadow: 'none',
              border: 0,
            }),
          }}
        >
          {/* Accent bar */}
          <Box
            sx={{
              height: '3px',
              flexShrink: 0,
              background: isPrintModal
                ? 'transparent'
                : `linear-gradient(90deg, ${mainColor}, ${secColor}, ${chroma(mainColor).brighten(0.6).css()})`,
            }}
          />
          {/* Header */}
          {(topTitle || title) && (
            <>
              <Box
                sx={{
                  ...glassChromeSurface(theme, { radius: 0 }),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  rowGap: '10px',
                  px: '20px',
                  pt: '16px',
                  pb: '12px',
                  flexShrink: 0,
                  border: 0,
                  boxShadow: 'none',
                  ...(isPrintModal && { background: theme.palette.background.paper }),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, mr: 1 }}>
                  {icon && (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: `${glassChromeRadii.control}px`,
                        background: `linear-gradient(135deg, ${chroma(mainColor).alpha(0.15).css()}, ${chroma(secColor).alpha(0.1).css()})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {React.cloneElement(icon, {
                        sx: { fontSize: 15, color: glassChromeControl(theme, { active: true }).color, ...(icon.props?.sx || {}) },
                      })}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                    {topTitle && title && (
                      <Text
                        value={topTitle}
                        fontSize={10}
                        fontWeight={600}
                        sx={{
                          color: textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          lineHeight: 1.2,
                        }}
                      />
                    )}
                    {typeof (title || topTitle) === 'string' ? (
                      <Text
                        value={title || topTitle}
                        fontSize={15}
                        fontWeight={600}
                        sx={{
                          display: 'block',
                          fontFamily: 'Montserrat',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      />
                    ) : (
                      title || topTitle
                    )}
                    {subtitle && (
                      <Text
                        value={subtitle}
                        fontSize={11}
                        fontWeight={400}
                        sx={{
                          display: 'block',
                          color: textMuted,
                          lineHeight: 1.3,
                          mt: '-1px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      />
                    )}
                  </Box>
                </Box>
                {tabs?.length > 0 && (
                  <SegmentedTabs
                    items={tabs}
                    value={activeTab}
                    onChange={onTabChange}
                    sx={isMobile ? {
                      order: 2,
                      flexBasis: '100%',
                      width: '100%',
                      minWidth: 0,
                      overflowX: 'auto',
                    } : { mr: '8px' }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, minWidth: 0, maxWidth: isMobile ? '70%' : undefined }}>
                  {headerExtras}
                  {showReset && onReset && (
                    <IconButton
                      size="small"
                      onClick={onReset}
                      title={t('reset') || 'Reset'}
                      aria-label={t('reset') || 'Reset'}
                      sx={controlStyles}
                    >
                      <RestartAlt sx={{ fontSize: 16, color: textMuted }} />
                    </IconButton>
                  )}
                  {!isMobile && (
                    <IconButton
                      size="small"
                      aria-label={isFullscreen ? t('exitFullscreen', 'Réduire la fenêtre') : t('fullscreen', 'Agrandir la fenêtre')}
                      onClick={() => setIsFullscreen((prev) => !prev)}
                      sx={controlStyles}
                    >
                      {isFullscreen ? (
                        <FullscreenExit sx={{ fontSize: 17, color: textMuted }} />
                      ) : (
                        <Fullscreen sx={{ fontSize: 17, color: textMuted }} />
                      )}
                    </IconButton>
                  )}
                  {!disableClose && (
                    <IconButton
                      size="small"
                      onClick={handleClose}
                      aria-label={t('close', 'Fermer')}
                      sx={controlStyles}
                    >
                      <Close
                        sx={{
                          fontSize: 15,
                          color: textMuted,
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  height: '1px',
                  mx: '20px',
                  flexShrink: 0,
                  backgroundColor: isDarkMode
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.05)',
                }}
              />
            </>
          )}

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: noScroll ? 'hidden' : 'auto',
              backgroundColor: theme.palette.background.paper,
              p: noPadding ? 0 : '20px',
              ...(height && !noPadding
                ? { height }
                : {}),
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                background: isDarkMode
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.08)',
              },
            }}
          >
            <ModalFullscreenContext.Provider value={isFullscreen}>
              {children}
            </ModalFullscreenContext.Provider>
          </Box>

          {/* Footer action bar */}
          {primaryAction && (
            <>
              <Box
                sx={{
                  height: '1px',
                  mx: '16px',
                  flexShrink: 0,
                  backgroundColor: isDarkMode
                    ? 'rgba(255,255,255,0.04)'
                    : 'rgba(0,0,0,0.04)',
                }}
              />
              <Box
                sx={{
                  ...glassChromeSurface(theme, { radius: 0 }),
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  px: '20px',
                  py: '14px',
                  border: 0,
                  boxShadow: 'none',
                  ...(isPrintModal && { background: theme.palette.background.paper }),
                }}
              >
                {!isMobile && (title || subtitle) && (
                  <Box sx={{ flex: 1, minWidth: 0, maxWidth: '50%', overflow: 'hidden' }}>
                    {title && (
                      <Text
                        value={title}
                        fontSize={13}
                        fontWeight={600}
                        sx={{
                          fontFamily: 'Montserrat',
                          lineHeight: 1.3,
                          letterSpacing: '-0.01em',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      />
                    )}
                    {subtitle && (
                      <Text
                        value={subtitle}
                        fontSize={11}
                        fontWeight={400}
                        sx={{
                          color: textMuted,
                          lineHeight: 1.3,
                          mt: '1px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      />
                    )}
                  </Box>
                )}

                {groups !== undefined && (
                  <Box
                    sx={{
                      flex: isMobile ? 1 : 'none',
                      width: isMobile ? 'auto' : '220px',
                      maxWidth: '300px',
                    }}
                  >
                    <GroupsSelectorElement
                      label={t('allowedGroups')}
                      value={groups}
                      onChange={handleGroupsChange}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: '8px',
                    flexShrink: 0,
                    marginLeft: 'auto',
                  }}
                >
                  {secondaryAction && (
                    <GlassButton
                      variant="text"
                      color="inherit"
                      onClick={onSecondaryAction}
                      disabled={disabledSec}
                      sx={{ width: 'auto', maxWidth: '150px', minHeight: 36, borderRadius: `${glassChromeRadii.control}px` }}
                    >
                      {secondaryAction}
                    </GlassButton>
                  )}
                  {primaryAction && (
                    <GlassButton
                      disabled={disabledPrim}
                      onClick={onPrimaryAction}
                      sx={{ width: 'auto', maxWidth: '150px', minHeight: 36, borderRadius: `${glassChromeRadii.control}px` }}
                    >
                      {primaryAction}
                    </GlassButton>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal></ModalHeaderContext.Provider>
  );
};

export default ModalTemplate;
